// Profil-Nachbau F-15 Punkt 7 (BAU-GELD.md 1.7): Spiegel von backend/src/administration/billing/kontingentMonat.ts;
// bei Änderungen beide nachziehen. Der Monat des Kontingents, der Recherchen und des Guthabens beginnt am 1. um 00:00
// Europe/Vienna.

export const KONTINGENT_ZEITZONE = 'Europe/Vienna';

const WANDUHR = new Intl.DateTimeFormat('en-US', {
  timeZone: KONTINGENT_ZEITZONE, hourCycle: 'h23',
  year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
});
const MONATSNAME = new Intl.DateTimeFormat('de-AT', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const MONAT = /^(\d{4})-(0[1-9]|1[0-2])$/;

type Wanduhr = { year: number; month: number; day: number; hour: number; minute: number; second: number };
const zwei = (n: number) => String(n).padStart(2, '0');
const jahrText = (n: number) => String(n).padStart(4, '0');

/** Wiener Kalendertag und Uhrzeit eines Zeitpunkts. */
function wanduhr(instant: Date): Wanduhr {
  const teile: Record<string, number> = {};
  for (const teil of WANDUHR.formatToParts(instant)) if (teil.type !== 'literal') teile[teil.type] = Number(teil.value);
  return teile as Wanduhr;
}

/** Versatz der Wiener Zeit gegen UTC am Zeitpunkt instant, in Millisekunden (MEZ 1 Stunde, MESZ 2 Stunden). */
function versatz(instant: Date): number {
  const w = wanduhr(instant);
  return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second) - Math.floor(instant.getTime() / 1000) * 1000;
}

function teileDes(monat: string): { jahr: number; monat: number } {
  const treffer = MONAT.exec(monat);
  if (!treffer) throw new RangeError(`Ungültiger Monat „${monat}“, erwartet JJJJ-MM.`);
  return { jahr: Number(treffer[1]), monat: Number(treffer[2]) };
}

/** 'JJJJ-MM' des Wiener Kalendertags von instant. */
export function kontingentMonat(instant: Date): string {
  const w = wanduhr(instant);
  return `${jahrText(w.year)}-${zwei(w.month)}`;
}

/** Der laufende Kontingentmonat (Wiener Zeit), wie der Server ihn bucht (billing/usage.ts monthAt). */
export const laufenderKostenMonat = (jetzt: Date = new Date()) => kontingentMonat(jetzt);

/** Zeitpunkt 1. des Monats 00:00 Europe/Vienna (als Date, also UTC-Zeitpunkt). Ungültiger Monat → RangeError. */
export function monatsBeginn(monat: string): Date {
  const { jahr, monat: m } = teileDes(monat);
  // Die Wiener Mitternacht, als UTC gelesen. Mitternacht am Monatsersten liegt nie in einer Umstellungsstunde; der Versatz
  // wird am gesuchten Zeitpunkt selbst einmal nachgeprüft.
  const mitternacht = Date.UTC(jahr, m - 1, 1);
  const geschaetzt = mitternacht - versatz(new Date(mitternacht));
  return new Date(mitternacht - versatz(new Date(geschaetzt)));
}

/** Beginn des nächsten Wiener Monats nach instant. */
export function naechsterMonatsBeginn(instant: Date): Date {
  const { jahr, monat } = teileDes(kontingentMonat(instant));
  return monatsBeginn(monat === 12 ? `${jahrText(jahr + 1)}-01` : `${jahrText(jahr)}-${zwei(monat + 1)}`);
}

/** Letzter Kalendertag des Monats als 'JJJJ-MM-TT'. */
export function letzterTag(monat: string): string {
  const { jahr, monat: m } = teileDes(monat);
  return `${monat}-${zwei(new Date(Date.UTC(jahr, m, 0)).getUTCDate())}`;
}

/** „Oktober 2026“, „Jänner 2027“ (de-AT). */
export function monatsName(monat: string): string {
  const { jahr, monat: m } = teileDes(monat);
  return MONATSNAME.format(new Date(Date.UTC(jahr, m - 1, 15)));
}

/** 'JJJJ-MM-TT' des Wiener Kalendertags (für Tagesrechnungen, etwa die Reichweite in PF-754). */
export function wienerTag(instant: Date): string {
  const w = wanduhr(instant);
  return `${jahrText(w.year)}-${zwei(w.month)}-${zwei(w.day)}`;
}
